import type { JSX } from 'react'
/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, { Component } from 'react'
import Icon, { IconType } from '../Icon'
import Video from '../Video'
import Label from '../Label'
import DevicePermissionsWarning from '../DevicePermissionsWarning'
import AudioAnalyser from '../AudioSpectrum/AudioAnalyser'
import inputPreviewStyles from './InputDeviceSelector.module.scss'
import styles from '@bt-react/styles'
import devices, { DeviceStats } from '@bt-react/libs/devices'
import { createLocalVideoTrack, createLocalAudioTrack, LocalAudioTrack, LocalVideoTrack, LocalVideoTrackPublication } from 'twilio-video'
import classnames from 'classnames'
import { InputDeviceSelectorProps, InputDeviceSelectorState } from './types'
import get from 'lodash/get'
import InputSourceSelector from '../InputSourceSelector'
import { Option } from '../InputSourceSelector/types'
import { captureException } from '@sentry/react'

class InputDeviceSelector extends Component<InputDeviceSelectorProps, InputDeviceSelectorState> {

  mounted = false

  constructor (props) {

    super(props)

    this.state = {
      selectedDevice: undefined,
      mediaTrack: undefined,
      inputDevices: [],
      showPermissionsWarning: false,
      inputDevicesOptions: this.props.options || [],
      videoTrackStarted: false,
      audioTrackStarted: false
    }

  }

  async componentDidMount () {

    this.mounted = true
    let permissionsChecked = false

    window.setTimeout(() => {

      if (!permissionsChecked) {

        this.setState({
          showPermissionsWarning: true
        })

      }

    }, 1200)

    try {

      const mediaTrack = this.props.deviceType === 'camera'
        ? await createLocalVideoTrack({ deviceId: this.props.selectedDeviceId, aspectRatio: 1.777777778 })
        : await createLocalAudioTrack({ deviceId: this.props.selectedDeviceId })

      this.addMediaListeners(mediaTrack)

      this.setState({
        mediaTrack
      })

    }
    catch (e) {

      captureException(e)

    }

    permissionsChecked = true
    let devicesList: DeviceStats[] = []
    
    try {
      
      devicesList = await devices.checkPermissions()

    }
    catch (e) {

      captureException(e)

    }

    const inputDevices = this.props.deviceType === 'camera' ? this.getVideoDevices(devicesList) : this.getAudioDevices(devicesList)

    if (this.mounted) {

      this.setState({
        showPermissionsWarning: !inputDevices.length,
        inputDevices,
        inputDevicesOptions: inputDevices.map(({ label, deviceId }) => ({ label, value: deviceId }))
      })

    }

    if (this.props.onListLoaded) this.props.onListLoaded()

  }

  componentDidUpdate(prevProps, prevState) {

    if (this.props.onVideoTrackLoad && !prevState.videoTrackStarted && this.state.videoTrackStarted) {

      this.props.onVideoTrackLoad(this.state.mediaTrack as LocalVideoTrack)

    }

  }

  componentWillUnmount () {

    this.removeMediaListeners(this.state.mediaTrack)
    this.removeTracks(this.state.mediaTrack)
    this.mounted = false

  }

  removeTracks = (mediaTrack) => {

    if (!mediaTrack) return

    try {

      mediaTrack.stop()

    }
    catch (e) {

      captureException(e)

    }

    try {

      mediaTrack.detach()

    }
    catch (e) {

      captureException(e)

    }

  }

  addMediaListeners = (mediaTrack) => {

    mediaTrack.addListener('started', this.addMediaStarted)
    mediaTrack.addListener('stopped', this.addMediaStopped)

  }

  addMediaStarted = (track) => {

    switch (track.kind) {

      case 'video':
        this.setState({ videoTrackStarted: true })
        break
      case 'audio':
        this.setState({ audioTrackStarted: true })
        break
      default:

    }

  }

  addMediaStopped = (track) => {

    switch (track.kind) {

      case 'video':
        this.setState({ videoTrackStarted: false })
        break
      case 'audio':
        this.setState({ audioTrackStarted: false })
        break
      default:
        break

    }

  }

  removeMediaListeners = (mediaTrack) => {

    if (!mediaTrack) return

    mediaTrack.removeListener('started', this.addMediaStarted)
    mediaTrack.removeListener('stopped', this.addMediaStopped)

  }

  onDeviceChange = async (selectedDevice) => {

    const selectedDeviceId = selectedDevice.value
    const selectedOption = this.state.inputDevices.find(({ deviceId }) => deviceId === selectedDeviceId)

    if (this.props.onDeviceChange) {

      if (selectedOption) this.props.onDeviceChange(selectedOption)

    }

    if (this.state.mediaTrack) {

      this.removeTracks(this.state.mediaTrack)
      this.removeMediaListeners(this.state.mediaTrack)
      this.setState({
        mediaTrack: undefined
      })
      if (this.props.onVideoTrackLoad) {
        this.props.onVideoTrackLoad(null)
      }

    }

    try {

      const mediaTrack = this.props.deviceType === 'camera'
        ? await createLocalVideoTrack({ deviceId: selectedDeviceId, aspectRatio: 1.7 })
        : await createLocalAudioTrack({ deviceId: selectedDeviceId })

      if (this.mounted) {

        this.addMediaListeners(mediaTrack)

        this.setState({
          mediaTrack,
          selectedDevice: selectedOption
        })

      }

    }
    catch (e) {

      captureException(e)

    }

  }

  getVideoDevices = (devices: DeviceStats[]) => {

    return devices
      .filter((device) => device.kind === 'videoinput')
      .sort(({ capabilities: capabilitiesA }, { capabilities: capabilitiesB }) => get(capabilitiesA, 'width.max', 0) > get(capabilitiesB, 'width.max', 0) ? -1 : 1)

  }

  getAudioDevices = (devices: DeviceStats[]) => {

    return devices
      .filter((device) => device.kind === 'audioinput' && device.deviceId !== 'default' && device.deviceId !== 'communications')

  }

  getPreSelectedDevice = (): Option | undefined => {

    if (!this.props.selectedDeviceId) return undefined
    return this.state.inputDevicesOptions.find(({ value }) => value === this.props.selectedDeviceId)

  }

  deviceWarning = () => (
    <div className={inputPreviewStyles['device-warning-container']}>
      {this.props.label && <Label>{this.props.label}</Label>}
      <DevicePermissionsWarning deviceType={this.props.deviceType} />
    </div>
  )

  selectorAndPreview = () => {

    return <InputSourceSelector
      label={this.props.label || ''}
      switchComponent={this.props.switchComponent}
      options={this.state.inputDevicesOptions}
      onOptionChange={this.onDeviceChange}
      sourceType={this.props.deviceType}
      previewComponent={this.props.previewComponent || (this.state.mediaTrack && this.getMediaTrackElement())}
      selectedOption={this.getPreSelectedDevice()}
      column={this.props.layout === 'column'}
      disabled={this.props.disabled}
      optionsPlaceholderText={this.props.optionsPlaceholderText}
      disabledText={this.props.disabledText}
    />

  }

  getMediaTrackElement = (): JSX.Element | undefined => {

    switch (this.props.deviceType) {

      case 'camera': {

        if (!this.state.videoTrackStarted) return

        const mediaTrack = this.state.mediaTrack as unknown as LocalVideoTrack
        const publication = {
          track: mediaTrack,
          trackName: 'videofeed',
          trackSid: 'videofeed'
        } as LocalVideoTrackPublication

        return <Video
          publication={publication}
          mirrorVideo={true}
          trackSid='videofeed'
        />

      }
      case 'microphone': {

        if (!this.state.audioTrackStarted) return

        const mediaTrack = this.state.mediaTrack as unknown as LocalAudioTrack

        return <div className={`${inputPreviewStyles['audio-spectrum']}`} >
          <Icon name={IconType.iconName.microphone} colour={IconType.colour.secondary} className={inputPreviewStyles['icon']}/>
          <AudioAnalyser
            // @ts-ignore
            audio={mediaTrack.attach().captureStream()}
            spectrumProps={{
              width: 60,
              height: 40,
              meterCount: 5,
              gap: 2,
              meterWidth: 10,
              meterColor: '#003CB2'
            }}
          />
        </div>

      }
      default:

    }

  }

  render () {

    return (
      <div data-bdd={`input-device-selector-${this.props.deviceType}`}
        className={classnames({
          [styles['flex-container']]: true,
          [inputPreviewStyles['input-selector']]: true,
          [inputPreviewStyles['input-selector-column-layout']]: this.props.layout === 'column'
        })}>
        {
          !this.state.showPermissionsWarning
            ? this.selectorAndPreview()
            : this.deviceWarning()
        }
      </div>
    )

  }

}

export default InputDeviceSelector
